import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Booking, Scope } from '../../../core/models';
import { DurationComponent } from './duration.component';
import { BusComponent } from './bus.component';
import { GuidesComponent } from './guides.component';
import { DriverinfosComponent } from './driverinfos.component';
import { RundownComponent } from './rundown.component';
import { InfodetailsComponent } from './infodetails.component';
import { BookingDataService } from '../../../services/booking-data/booking-data.service';
import { SimpleShipSelectComponent } from '../../modals/simple-ship-select.component';
import {
  patchBookingForm,
  formatUnixToTimeString,
  convertFormToBooking,
  stringToFirestoreTimestamp,
} from '../../../core/booking-from.utils';
import { BookingService } from '../../../services/resources/booking.service';
import { distinctUntilChanged, first, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Timestamp } from '@angular/fire/firestore';
import { VoucherAddonTextComponent } from './voucher-addon-text.component';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AccountingAddonTextComponent } from './accounting-addon-text.component';
import { LoadingBarrierService } from '../../../services/resources/loading-barrier.service';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'app-booking-editor',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DurationComponent,
    BusComponent,
    GuidesComponent,
    DriverinfosComponent,
    RundownComponent,
    InfodetailsComponent,
    SimpleShipSelectComponent,
    VoucherAddonTextComponent,
    AccountingAddonTextComponent,
  ],
  standalone: true,
  template: `
    <div
      *ngIf="loadedBooking"
      class="mx-auto max-w-7xl pt-4 px-3 sm:pt-6 sm:px-6 lg:px-8 pb-24"
    >
      <form
        [formGroup]="bookingForm"
        (ngSubmit)="onSubmit()"
        class="space-y-12"
      >
        <!-- General Information -->
        <div
          class="flex flex-col lg:flex-row lg:justify-around gap-y-8 lg:gap-y-0 border-b border-gray-900/10 pb-8 sm:pb-12"
        >
          <div class="w-full lg:w-auto">
            <h2 class="text-base font-semibold leading-7 text-gray-900">
              Buchungsinformationen für
              {{ loadedBooking.ship!.shipName }} von
              {{ loadedBooking.ship!.companyName }}
            </h2>
            <p class="mt-1 text-sm leading-6 text-gray-600">
              Alle Informationen für die Buchunng.
            </p>
            <app-infodetails [cruiseDetails]="cruiseDetails" />
          </div>
          <div>
            <!-- Duration -->
            <app-duration
              [duration]="duration"
              [guideAmount]="guideAmount"
              (patchGuides)="onPatchGuides($event)"
            />

            <app-accounting-addon-text
              *ngIf="user"
              [accounting]="accounting"
              [currentUser]="{ id: user.id, name: user.username }"
            />
          </div>
        </div>
        <div
          class="flex flex-col lg:flex-row lg:justify-around lg:items-start gap-y-8 lg:gap-y-0 border-b border-gray-900/10 pb-8 sm:pb-12"
        >
          <app-bus
            [busInfo]="busInfo"
            (busCompanyChanged)="onBusCompanyChanged($event)"
          />

          <app-driverinfos
            [driverInfo]="driverInfo"
            [company]="busCompany"
            [shipInfo]="loadedBooking.ship"
          />
        </div>
        <div
          class="relative border-b border-gray-900/10 pb-12"
          [class.min-h-[16rem]]="!editorReady()"
        >
          <div
            *ngIf="!editorReady()"
            class="absolute inset-0 z-50 grid place-items-center bg-white/70 "
            aria-busy="true"
          >
            <div class="flex justify-center items-center h-64">
              <div
                class="flex flex-row items-center gap-x-2 italic text-gray-400"
              >
                <svg
                  aria-hidden="true"
                  class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-400 fill-columbus-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                Loading Guides...
                <span class="sr-only">Loading Guides...</span>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col lg:flex-row lg:justify-center pb-8 sm:pb-12"
          >
            <app-guides
              #guidesComponent
              [guides]="guides"
              [startTime]="startTime"
            />
          </div>
        </div>
        <div
          class="flex flex-col lg:flex-row gap-y-6 lg:gap-x-8 border-b border-gray-900/10 pb-8 sm:pb-12"
        >
          <app-rundown [rundown]="rundown" class="block w-full lg:flex-1" />
          <app-voucher-addon-text
            [addonText]="addonText"
            class="block w-full lg:flex-1"
          />
        </div>

        <!-- Actions: fixed bottom bar on mobile, inline on desktop -->
        <div
          class="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-end gap-x-3 bg-white px-4 py-3 border-t border-gray-200 shadow-lg
    sm:static sm:bg-transparent sm:px-0 sm:py-0 sm:border-0 sm:shadow-none sm:mt-6 sm:gap-x-6"
        >
          <button
            type="button"
            (click)="onCancel()"
            class="flex-1 sm:flex-none rounded-md bg-white px-4 py-2.5 sm:px-2.5 sm:py-1.5 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="!bookingForm.valid"
            class="flex-1 sm:flex-none rounded-md bg-columbus-blue-600 disabled:opacity-25 px-4 py-2.5 sm:px-3 sm:py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Save
          </button>
        </div>
      </form>
    </div>

    <div *ngIf="!loadedBooking">
      <div
        class="mx-auto max-w-7xl flex flex-row justify-center sm:justify-end px-4 sm:px-6 lg:px-8 pt-6"
      >
        <button
          (click)="toggleNewBookingModal()"
          type="button"
          class="rounded-md bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
        >
          Neue Buchung
        </button>
        <!-- Content goes here -->
      </div>

      <div *ngIf="newBookingToggler">
        <app-simple-ship-select (close)="onShipSelect($event)" />
      </div>
    </div>
  `,
  styles: ``,
  animations: [],
})
export class BookingEditorComponent implements OnInit, OnDestroy {
  @ViewChild('guidesComponent') guidesComponent!: GuidesComponent;

  fb = inject(FormBuilder);
  route = inject(ActivatedRoute);
  router = inject(Router);
  bookingDataService = inject(BookingDataService);
  bookingService = inject(BookingService);
  protected readonly barrier = inject(LoadingBarrierService);
  private readonly destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);

  editorScope: Scope = 'editor';
  editorReady = this.barrier.isScopeReady(this.editorScope);
  busCompany: string | undefined = undefined;

  loadedBooking: Booking | undefined = undefined;
  newBookingToggler = false;

  user = this.auth.currentUserSig();

  bookingForm = this.fb.group({
    id: [''],
    shipId: ['', Validators.required],
    createdDate: ['', Validators.required],
    userCreatorId: ['', Validators.required],
    userCreatorName: ['', Validators.required],
    //lastEditDate: ['', Validators.required],
    //userLastEditorId: ['', Validators.required],
    //userLastEditorName: ['', Validators.required],
    pdfUrl: [''],
    busInfo: this.fb.group({
      busCompany: ['-'],
      busSelected: this.fb.array([]),
    }),
    guides: this.fb.array([]),
    cruiseDetails: this.fb.group({
      jetty: [''],
      cruiseManager: [''],
      cruiseManagerTel: [''],
      paxShip: [''],
      paxTour: [''],
      internNote: [''],
      invoiceNumber: [''],
    }),

    duration: this.fb.group({
      tourCode: ['', Validators.required],
      date: ['', Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
    }),
    driverInfo: this.fb.group({
      url: [''],
      customerNumber: [''],
      orderNumber: [''],
      drivers: this.fb.array([]),
    }),
    rundown: this.fb.group({
      text: [''],
    }),
    voucher: this.fb.group({
      addonText: [''],
    }),
    accounting: this.fb.group({
      note: [''],
      billed: [''],
      dateBilled: [''],
      userBilledId: [''],
      userBilledName: [''],
    }),
  });

  isReady = false;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((pm) => ({
          cw: pm.get('calendarWeek'),
          shipId: pm.get('shipId'),
          bookingId: pm.get('bookingId'),
        })),
        distinctUntilChanged(
          (a, b) =>
            a.cw === b.cw &&
            a.shipId === b.shipId &&
            a.bookingId === b.bookingId,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ cw, shipId, bookingId }) => {
        this.barrier.resetScope(this.editorScope);
        this.bookingForm.reset();

        const dataBooking = this.bookingDataService.booking() as Booking;
        if (dataBooking) {
          this.loadedBooking = dataBooking;
          this.initDuration(this.loadedBooking);
          this.patchForm(this.loadedBooking);
          return;
        }

        if (cw && shipId && bookingId) {
          this.loadBookingFromUrlParams(cw, shipId, bookingId);
          return;
        }
      });

    /*
    this.loadedBooking = this.bookingDataService.booking() as Booking;
    if (this.loadedBooking) {
      this.initDuration(this.loadedBooking);
      this.patchForm(this.loadedBooking);
    } else {
      this.route.paramMap.pipe(first()).subscribe((params) => {
        const calendarWeek = params.get('calendarWeek');
        const shipId = params.get('shipId');
        const bookingId = params.get('bookingId');
        if (calendarWeek && shipId && bookingId && !this.loadedBooking) {
          this.loadBookingFromUrlParams(calendarWeek, shipId, bookingId);
        }
      });
    }*/
  }
  ngOnDestroy(): void {
    this.barrier.resetScope(this.editorScope);
  }

  get startTime() {
    const date = (this.bookingForm.get('duration') as FormGroup).get(
      'date',
    )?.value;

    const starttime = (this.bookingForm.get('duration') as FormGroup).get(
      'startTime',
    )?.value;

    return stringToFirestoreTimestamp(date, starttime);
  }

  get guideAmount() {
    return (this.bookingForm.get('guides') as FormArray).length;
  }

  get rundown() {
    return this.bookingForm.get('rundown') as FormGroup;
  }

  get addonText() {
    return this.bookingForm.get('voucher') as FormGroup;
  }
  get accounting() {
    return this.bookingForm.get('accounting') as FormGroup;
  }
  get busInfo() {
    return this.bookingForm.get('busInfo') as FormGroup;
  }

  get guides() {
    return this.bookingForm.get('guides') as FormArray;
  }

  get driverInfo() {
    return this.bookingForm.get('driverInfo') as FormGroup;
  }
  patchForm(booking: Booking): void {
    this.onBusCompanyChanged(booking.busInfos?.busCompany);
    //    console.log('patchform', booking);
    patchBookingForm(this.bookingForm, booking, this.fb);
  }

  toggleNewBookingModal() {
    this.newBookingToggler = !this.newBookingToggler;
  }

  onShipSelect(event: boolean) {
    if (event) {
      this.ngOnInit();
    }

    this.toggleNewBookingModal();
  }

  initDuration(booking: Booking) {
    if (booking && booking.duration.startTime && booking.duration.startTime) {
      const data = {
        date: new Date(booking.duration.startTime.seconds * 1000)
          .toISOString()
          .split('T')[0],
        startTime: formatUnixToTimeString(booking.duration.startTime.seconds),
        endTime: formatUnixToTimeString(booking.duration.endTime.seconds),
      };
      this.duration.patchValue(data);
    }
  }

  get cruiseDetails() {
    return this.bookingForm.get('cruiseDetails') as FormGroup;
  }

  get duration() {
    return this.bookingForm.get('duration') as FormGroup;
  }

  onSubmit() {
    if (this.loadedBooking) {
      const filledBooking = convertFormToBooking(
        this.bookingForm,
        this.loadedBooking,
      );
      this.bookingDataService.setBooking(filledBooking);
      if (!filledBooking.id) {
        //console.log('creating NEW Booking');
        this.bookingService.createNewBooking(filledBooking);
      } else {
        // console.log('Updating Booking');
        this.bookingService.updateBookingChanges(filledBooking);
      }

      this.router.navigate(['/booking/pdf-gen']);
    }
  }
  onCancel() {
    this.router.navigate(['/dashboard']);
  }

  loadBookingFromUrlParams(
    calendarWeek: string,
    shipId: string,
    bookingId: string,
  ) {
    this.bookingService
      .getBookingData(calendarWeek, shipId, bookingId)
      .pipe(first())
      .subscribe((booking) => {
        if (booking) {
          this.loadedBooking = booking;
          this.patchForm(booking);
        }
      });
  }

  onBusCompanyChanged(company: string | undefined) {
    if (company === 'Dr. Richard') {
      // Patch specific driver info for Dr. Richard
      this.busCompany = company;
    } else {
      // Patch specific driver info for Dr. Richard
      this.busCompany = undefined;
    }
  }

  onPatchGuides(event: Timestamp) {
    if (event) {
      this.guidesComponent.updateTime(event);
    }
  }
}
